import { Delete, Edit } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { Box, Button } from "@mui/material";
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import {
	useContactsByIdsLazyQuery,
	useDeleteAdditionalInformationMutation,
	useUpdateContactPersonInformationMutation,
	useCreateContactPersonInformationMutation,
	useGetAllMembersOfLazyQuery,
	useDeleteMemberMutation,
	Contact,
	ContactUsage,
	ContactType,
} from "@repo/server-api";
import {
	ContactExtended,
	ContactPersonInformation,
	EmailInformation,
	PhoneInformation,
	RuleFactoryType,
} from "@repo/server-api/types";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { useEffect, useMemo, useState } from "react";

import { parseContactPersonIDs, setContactValues } from "@repo/legacy/components/helper/Index";
import ConformationDialog from "@repo/legacy/components/shared/dialogs/Conformation";

import DialogContactPerson from "../dialogs/DialogContactPerson";
import DialogExistingContactPerson from "../dialogs/DialogExistingContactPerson";
import DialogMailbox from "../dialogs/DialogMailbox";

// Combined row type for contact persons and mailboxes
type CombinedRow = ContactExtended & { rowType: "contact-person" | "mailbox" };

// Extracted component to properly use React hooks
type ContactPersonActionCellProps = {
	row: CombinedRow;
	onEditContactPerson: (contactPerson: ContactExtended) => void;
	onEditMailbox: (mailbox: ContactExtended) => void;
	onDeleteContactPerson: (contactPerson: ContactExtended) => void;
	onDeleteMailbox: (mailbox: ContactExtended) => void;
};

function ContactPersonActionCell({
	row,
	onEditContactPerson,
	onEditMailbox,
	onDeleteContactPerson,
	onDeleteMailbox,
}: ContactPersonActionCellProps) {
	const { t } = useTranslation();
	const [openDeleteConformation, setOpenDeleteConformation] = useState<boolean>(false);
	const isMailbox = row.rowType === "mailbox";

	const handleYes = () => {
		if (isMailbox) {
			onDeleteMailbox(row);
		} else {
			onDeleteContactPerson(row);
		}
		setOpenDeleteConformation(false);
	};

	const openDelete = () => {
		setOpenDeleteConformation(true);
	};

	const openEdit = () => {
		if (isMailbox) {
			onEditMailbox(row);
		} else {
			onEditContactPerson(row);
		}
	};

	return (
		<div>
			<Button data-testid="edit-button" onClick={openEdit}>
				<Edit />
			</Button>
			<Button data-testid="delete-button" onClick={openDelete}>
				<Delete />
			</Button>
			<ConformationDialog
				text={isMailbox ? t("mailbox.delete-confirmation") : t("do-you-want-to-delete-contact-person")}
				setOpenDeleteConformation={setOpenDeleteConformation}
				openPopup={openDeleteConformation}
				handleYes={handleYes}
			/>
		</div>
	);
}

export default function ContactPersons(
	props: Readonly<{
		contact: Contact | null;
		setContact: (contact: Contact) => void;
		ruleFactory: RuleFactoryType;
	}>,
) {
	const contact = useMemo<Contact | null>(() => props.contact, [props.contact]);
	const [informationId, setInformationId] = useState<string>("");
	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [openExistingDialog, setOpenExistingDialog] = useState<boolean>(false);
	const [contactPerson, setContactPerson] = useState<ContactExtended | null>(null);
	const [contactPersons, setContactPersons] = useState<ContactExtended[]>([]);

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	// Mailbox state
	const [openMailboxDialog, setOpenMailboxDialog] = useState<boolean>(false);
	const [mailbox, setMailbox] = useState<ContactExtended | null>(null);
	const [mailboxes, setMailboxes] = useState<ContactExtended[]>([]);

	const { showNotification } = useNotification();

	const [createContactPersonInformation] = useCreateContactPersonInformationMutation({
		onCompleted(data) {
			const c = data?.createContactPersonInformation?.contactPersonInformation;
			if (c) {
				const newContact: Contact = {
					...setContactValues(contact),
				};
				newContact.additionalInformation = newContact.additionalInformation
					? [
							...newContact.additionalInformation,
							{
								...c,
								__typename: "ContactPersonInformation",
							} as ContactPersonInformation,
						]
					: [
							{
								...c,
								__typename: "ContactPersonInformation",
							} as ContactPersonInformation,
						];
				props.setContact(newContact);
				getAllContacts();
				showNotification(t("contact-created"), "success");
				setSaveClicked(false);
			}
		},
		onError(error: { message: string }) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const [updateContactPersonInformation] = useUpdateContactPersonInformationMutation({
		onCompleted(data) {
			const c = data?.updateContactPersonInformation?.contactPersonInformation;
			if (c) {
				const newContact = {
					...setContactValues(contact),
				};
				newContact.additionalInformation = newContact.additionalInformation
					? newContact.additionalInformation.map((i) => {
							if (i.id === c.id) {
								return { ...c, __typename: "ContactPersonInformation" };
							}
							return i;
						})
					: [];
				props.setContact(newContact);
				getAllContacts();
				showNotification(t("contact.updated"), "success");
				setSaveClicked(false);
			}
		},
		onError(error: { message: string }) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const [deleteContactPerson] = useDeleteAdditionalInformationMutation({
		onCompleted(data) {
			if (data.deleteAdditionalInformation?.boolean) {
				const newContact = {
					...setContactValues(contact),
				};
				newContact.additionalInformation = newContact.additionalInformation?.filter(
					(i) => i.id !== informationId,
				);
				props.setContact(newContact);
				showNotification(t("contact.deleted"), "success");
			}
		},
		onError(error: { message: string }) {
			showNotification(error.message, "error");
		},
	});

	const [getAllContacts] = useContactsByIdsLazyQuery({
		variables: { ids: parseContactPersonIDs(contact) },
		fetchPolicy: "no-cache",
		onCompleted(data: { contactsByIds: Contact[] | null }) {
			if (data && data.contactsByIds) {
				setContactPersons(
					data.contactsByIds
						// Filter out mailboxes - they are loaded separately via getAllMailboxes
						.filter((c: Contact) => !c.contactTypes?.includes(ContactType.Mailbox))
						.map((c: Contact) => {
							const item = contact?.additionalInformation.find((info) => {
								return (
									info.__typename === "ContactPersonInformation" &&
									String((info as ContactPersonInformation).contactPersonId) === c.id
								);
							});
							return {
								...c,
								contactUsage: item?.contactUsage,
								informationId: item?.id,
							};
						}),
				);
			}
		},
	});

	// Mailbox queries and mutations
	const [deleteMailboxMember] = useDeleteMemberMutation({
		onCompleted(data) {
			if (data.deleteMember?.boolean) {
				showNotification(t("mailbox.deleted"), "success");
			}
		},
		onError(error: { message: string }) {
			showNotification(error.message, "error");
		},
	});

	const [getAllMailboxes] = useGetAllMembersOfLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data?.allMembersOf?.nodes) {
				// Map mailboxes with contactUsage from company's additionalInformation
				setMailboxes(
					data.allMembersOf.nodes.map((c) => {
						// Find ContactPersonInformation for this mailbox in parent company
						const item = contact?.additionalInformation?.find((info) => {
							return (
								info.__typename === "ContactPersonInformation" &&
								String((info as ContactPersonInformation).contactPersonId) === c.id
							);
						});
						return {
							...c,
							contactUsage: (item as ContactPersonInformation)?.contactUsage,
							informationId: item?.id,
						} as ContactExtended;
					}),
				);
			}
		},
	});

	const handleMailboxRefresh = (updatedMailbox: ContactExtended) => {
		const index = mailboxes.findIndex((m) => m.id === updatedMailbox.id);
		if (index > -1) {
			// Update existing mailbox
			const updated = [...mailboxes];
			updated[index] = updatedMailbox;
			setMailboxes(updated);
		} else {
			// New mailbox - add directly to the list (don't call refreshMailboxes
			// because contact.additionalInformation doesn't have the new ContactPersonInformation yet)
			setMailboxes([...mailboxes, updatedMailbox]);
		}
	};

	const handleDeleteMailbox = async (mailboxToDelete: ContactExtended) => {
		await deleteMailboxMember({
			variables: {
				memberId: mailboxToDelete.id,
				organizationId: contact?.id ?? "",
			},
		});
		setMailboxes(mailboxes.filter((m) => m.id !== mailboxToDelete.id));
	};

	useEffect(() => {
		getAllContacts();
	}, []);

	useEffect(() => {
		if (contact?.id) {
			getAllMailboxes({
				variables: {
					contactId: contact.id,
					types: [ContactType.Mailbox],
					first: 100,
				},
			});
		}
	}, [contact?.id]);

	const { t } = useTranslation();

	// Combined list of contact persons and mailboxes
	const combinedRows = useMemo<CombinedRow[]>(() => {
		const contactPersonRows: CombinedRow[] = contactPersons.map((cp) => ({
			...cp,
			rowType: "contact-person" as const,
		}));
		const mailboxRows: CombinedRow[] = mailboxes.map((mb) => ({
			...mb,
			rowType: "mailbox" as const,
		}));
		return [...contactPersonRows, ...mailboxRows];
	}, [contactPersons, mailboxes]);

	const columns: GridColDef<CombinedRow>[] = useMemo(
		() => [
			{
				field: "rowType",
				headerName: t("type"),
				width: 150,
				renderCell: (params: GridCellParams) => {
					return params.row.rowType === "mailbox"
						? t("mailbox")
						: t("contact-person");
				},
			},
			{
				field: "displayName",
				headerName: t("name"),
				width: 150,
				flex: 1,
				renderCell: (params: GridCellParams) => {
					return params.row.displayName || params.row.companyName;
				},
			},
			{
				field: "email",
				headerName: t("email"),
				width: 300,
				flex: 1,
				renderCell: (params: GridCellParams) => {
					return (params.row.additionalInformation ?? [])
						.filter(
							(e: { __typename?: string }): e is EmailInformation =>
								e.__typename === "EmailInformation",
						)
						.map((e: { emailAddress: string }) => e.emailAddress)
						.join(", ");
				},
			},
			{
				field: "phone",
				headerName: t("phone"),
				width: 200,
				renderCell: (params: GridCellParams) => {
					return (params.row.additionalInformation ?? [])
						.filter(
							(e: { __typename?: string }): e is PhoneInformation =>
								e.__typename === "PhoneInformation",
						)
						.map((e: { number: string }) => e.number)
						.join(", ");
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				width: 150,
				renderCell: (params) => (
					<ContactPersonActionCell
						row={params.row as CombinedRow}
						onEditContactPerson={(cp) => {
							setContactPerson(cp);
							setOpenDialog(true);
						}}
						onEditMailbox={(mb) => {
							setMailbox(mb);
							setOpenMailboxDialog(true);
						}}
						onDeleteContactPerson={(cp) => {
							setInformationId(cp.informationId ?? "");
							deleteContactPerson({
								variables: { contactId: contact?.id ?? "", informationId: cp.informationId ?? "" },
							});
						}}
						onDeleteMailbox={handleDeleteMailbox}
					/>
				),
			},
		],
		[t],
	);

	const handleContactPerson = (contactPerson: ContactExtended) => {
		const index = contactPersons.findIndex((c) => c.id === contactPerson?.id);
		if (index > -1 && contactPerson?.informationId) {
			updateContactPersonInformation({
				variables: {
					contactId: contact?.id ?? "",
					contactPersonId: contactPerson?.id ?? "",
					contactUsage: (contactPerson?.contactUsage as ContactUsage[]) ?? [],
					id: contactPerson?.informationId ?? "",
				},
			});
		} else {
			createContactPersonInformation({
				variables: {
					contactId: contact?.id ?? "",
					contactPersonId: contactPerson?.id ?? "",
					contactUsage: (contactPerson?.contactUsage as ContactUsage[]) ?? [],
				},
			});
		}
	};

	return (
		<Box sx={{ width: "100%" }}>
			<Box className="clear">
				<Button
					variant="contained"
					className="greenButton"
					data-testid="add-contact-person-button"
					onClick={() => {
						setContactPerson(null);
						setOpenDialog(true);
					}}>
					{t("company.add-contact-person")} <AddIcon />
				</Button>
				{contact?.contactTypes?.includes(ContactType.Client) === false && (
					<Button
						variant="contained"
						className="greenButton"
						onClick={() => {
							setContactPerson(null);
							setOpenExistingDialog(true);
						}}>
						{t("add-existing-contact-person")} <AddIcon />
					</Button>
				)}
				<Button
					variant="contained"
					className="greenButton"
					data-testid="add-mailbox-button"
					onClick={() => {
						setMailbox(null);
						setOpenMailboxDialog(true);
					}}>
					{t("company.add-mailbox")} <AddIcon />
				</Button>
			</Box>
			<DialogContactPerson
				contact={contactPerson}
				setContact={setContactPerson}
				refreshData={handleContactPerson}
				setOpen={setOpenDialog}
				open={openDialog}
				ruleFactory={props.ruleFactory}
				companyId={contact?.id || ""}
				saveClicked={saveClicked}
				setSaveClicked={setSaveClicked}
			/>
			<DialogExistingContactPerson
				contact={contactPerson}
				setContact={setContactPerson}
				refreshData={handleContactPerson}
				setOpen={setOpenExistingDialog}
				open={openExistingDialog}
				ruleFactory={props.ruleFactory}
				companyId={contact?.id || ""}
				saveClicked={saveClicked}
				setSaveClicked={setSaveClicked}
			/>
			<DialogMailbox
				mailbox={mailbox}
				setMailbox={setMailbox}
				refreshData={handleMailboxRefresh}
				setOpen={setOpenMailboxDialog}
				open={openMailboxDialog}
				companyId={contact?.id || ""}
			/>
			{combinedRows.length > 0 && (
				<Box sx={{ height: "100%", width: "100%", minHeight: "500px" }}>
					<DataGridPremium
						getRowId={(row) => row.id}
						rows={combinedRows}
						columns={columns as GridCellParams[]}
						disableRowSelectionOnClick
						className="table"
						hideFooter={true}
					/>
				</Box>
			)}
		</Box>
	);
}
